import type {
  AnalyticsWindow,
  MainnetCampaignCollection,
  MainnetCampaignsResponse,
  MainnetDashboardResponse,
  MainnetLeaderboardEntry,
  MainnetPartnerCampaign,
  MainnetPartnerDetailResponse,
  MainnetPartnerId,
  MainnetPartnerOverviewResponse,
  MainnetPartnerTier,
  MainnetPartnerTierResponse,
} from "@/types/mainnet-campaign";

function toNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined) return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toPartnerId(value: string): MainnetPartnerId {
  return value as MainnetPartnerId;
}

export function shortenAddress(address: string) {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function mapTier(
  tier: MainnetPartnerTierResponse,
  userTotalVolumeUsd: number,
): MainnetPartnerTier {
  const thresholdUsd = toNumber(tier.thresholdUsd);
  const progressUsd =
    tier.state === "locked"
      ? toNumber(tier.progressUsd)
      : thresholdUsd;
  const progressPercent =
    thresholdUsd > 0
      ? Math.min(100, (progressUsd / thresholdUsd) * 100)
      : 100;

  return {
    tierId: tier.tierId,
    order: tier.order,
    label: tier.label,
    thresholdUsd,
    shareMultiplier: toNumber(tier.shareMultiplier),
    state: tier.state,
    claimTaskGuid: tier.claimTaskGuid,
    crossedAt: tier.crossedAt,
    capturedBoostMultiplier:
      tier.capturedBoostMultiplier !== null
        ? toNumber(tier.capturedBoostMultiplier)
        : null,
    pointsPreview: toNumber(tier.pointsPreview),
    pointsAwarded: tier.pointsAwarded !== null ? toNumber(tier.pointsAwarded) : null,
    claimedAt: tier.claimedAt,
    shareUnitsAwarded:
      tier.shareUnitsAwarded !== null ? toNumber(tier.shareUnitsAwarded) : null,
    progressUsd:
      tier.state === "locked" ? progressUsd : Math.max(progressUsd, userTotalVolumeUsd),
    progressPercent,
    remainingUsd: tier.remainingUsd !== null ? toNumber(tier.remainingUsd) : null,
  };
}

export function mapPartnerCampaign(
  overview: MainnetPartnerOverviewResponse,
  detail: MainnetPartnerDetailResponse,
): MainnetPartnerCampaign {
  const tiers = detail.tiers
    .map((tier) => mapTier(tier, toNumber(detail.volume.userTotalUsd)))
    .sort((a, b) => a.order - b.order);
  const nextTier = tiers.find((tier) => tier.state === "locked") ?? null;
  const totalUserVolumeUsd = toNumber(detail.volume.userTotalUsd);
  const totalCampaignVolumeUsd = toNumber(detail.volume.totalCampaignUsd);

  const analytics = {
    "24h": {
      window: "24h" as AnalyticsWindow,
      userVolumeUsd: toNumber(detail.volume.user24hUsd),
      totalCampaignVolumeUsd: toNumber(detail.volume.campaign24hUsd),
    },
    "7d": {
      window: "7d" as AnalyticsWindow,
      userVolumeUsd: toNumber(detail.volume.user7dUsd),
      totalCampaignVolumeUsd: toNumber(detail.volume.campaign7dUsd),
    },
    total: {
      window: "total" as AnalyticsWindow,
      userVolumeUsd: totalUserVolumeUsd,
      totalCampaignVolumeUsd,
    },
  };

  return {
    partnerId: toPartnerId(detail.partnerId),
    name: detail.name,
    shortDescription: detail.brand.description,
    description: detail.brand.description,
    chainName: detail.chain.name,
    chainShortName: detail.chain.shortName,
    chainLogoUrl: detail.chain.logoUrl,
    accentColor: detail.brand.color,
    heroImageUrl: detail.brand.heroImageUrl,
    poolTokenSymbol: detail.pool.tokenSymbol,
    poolTokenName: detail.pool.tokenName,
    poolDisplayText: detail.pool.displayText,
    poolTotalAllocation: detail.pool.totalAllocation,
    boostMultiplier: detail.boost.isActive ? toNumber(detail.boost.multiplier) : null,
    boostExpiresAt: detail.boost.endsAt,
    isBoostActive: detail.boost.isActive,
    claimableTierCount: tiers.filter((tier) => tier.state === "claimable").length,
    claimedPointsTotal: toNumber(overview.user.claimedPoints),
    totalUserVolumeUsd,
    totalCampaignVolumeUsd,
    nextTierId: nextTier?.tierId ?? null,
    nextTierLabel: nextTier?.label ?? null,
    nextTierThresholdUsd: nextTier?.thresholdUsd ?? null,
    nextTierProgressUsd: nextTier ? Math.min(totalUserVolumeUsd, nextTier.thresholdUsd) : null,
    nextTierProgressPercent: nextTier ? detail.volume.nextTierProgressPercent : null,
    analytics,
    tiers,
  };
}

export function createMainnetCampaignCollection(
  overviewResponse: MainnetCampaignsResponse,
  details: MainnetPartnerDetailResponse[],
): MainnetCampaignCollection {
  const detailByPartner = new Map(details.map((detail) => [detail.partnerId, detail]));
  const campaigns = overviewResponse.partners
    .map((overview) => {
      const detail = detailByPartner.get(overview.partnerId);
      if (!detail) return null;
      return mapPartnerCampaign(overview, detail);
    })
    .filter((campaign): campaign is MainnetPartnerCampaign => campaign !== null);

  return {
    campaigns,
    lastUpdatedAt: new Date().toISOString(),
  };
}

export function mapDashboardPartnerSummary(partner: MainnetPartnerOverviewResponse) {
  return {
    partnerId: toPartnerId(partner.partnerId),
    name: partner.name,
    description: partner.brand.description,
    claimedPoints: toNumber(partner.user.claimedPoints),
    claimablePoints: toNumber(partner.user.claimablePoints),
    claimedTierCount: partner.user.claimedTierCount,
    claimableTierCount: partner.user.claimableTierCount,
    totalUserVolumeUsd: toNumber(partner.user.volume.totalUsd),
    last24hVolumeUsd: toNumber(partner.user.volume.last24hUsd),
    last7dVolumeUsd: toNumber(partner.user.volume.last7dUsd),
    boostMultiplier: partner.boost.isActive ? toNumber(partner.boost.multiplier) : null,
    boostExpiresAt: partner.boost.endsAt,
    isBoostActive: partner.boost.isActive,
  };
}

export function mapLeaderboardEntry(entry: {
  address: string;
  totalPoints: string;
  rank?: number;
  name?: string;
  photoUrl?: string;
  partnerScores?: Record<string, string>;
}): MainnetLeaderboardEntry {
  return {
    rank: entry.rank ?? 0,
    address: entry.address,
    displayName: entry.name?.trim() || shortenAddress(entry.address),
    avatarUrl: entry.photoUrl ?? null,
    score: toNumber(entry.totalPoints),
    scopedScore: toNumber(entry.totalPoints),
    tiersClaimed: null,
    partnerScores: {
      reppo: toNumber(entry.partnerScores?.reppo),
      zentra: toNumber(entry.partnerScores?.zentra),
    },
  };
}

export function mapScoresFromDashboard(response: MainnetDashboardResponse) {
  const partners = response.partners.map(mapDashboardPartnerSummary);
  const activeBoosts = partners
    .filter((partner) => partner.isBoostActive && partner.boostMultiplier && partner.boostExpiresAt)
    .map((partner) => ({
      partnerId: partner.partnerId,
      name: partner.name,
      multiplier: partner.boostMultiplier as number,
      expiresAt: partner.boostExpiresAt as string,
    }));

  return {
    legacyScore: toNumber(response.legacyScore),
    mainnetScore: toNumber(response.mainnetScore),
    claimableNowCount: partners.reduce((sum, partner) => sum + partner.claimableTierCount, 0),
    activeBoosts,
    partners,
  };
}
